import { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { useAppLayout } from '@core/contexts/LayoutContext';
import PageSearch, { type PageSearchConfig } from './PageSearch';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  searchConfig?: PageSearchConfig;
  maxWidth?: number | string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  action,
  searchConfig,
  maxWidth,
}) => {
  const { isMobile, isWebTwo } = useAppLayout();

  const titleBlock = (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="h4"
        fontWeight={700}
        mb={subtitle && !isMobile && !isWebTwo ? 1 : 0}
      >
        {title}
      </Typography>
      {subtitle && !isMobile && !isWebTwo && (
        <Typography variant="subtitle1" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  const actionBlock = action ? (
    <Box sx={{ flexShrink: 0 }}>{action}</Box>
  ) : null;

  const searchBlock = searchConfig ? (
    <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
      <PageSearch {...searchConfig} size={isWebTwo ? 'sm' : 'md'} />
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        mb: isMobile ? 2 : isWebTwo ? 0 : 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        ...(maxWidth && { maxWidth }),
      }}
    >
      {isMobile ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            {titleBlock}
            {actionBlock}
          </Box>
          {searchBlock}
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {titleBlock}
          {(searchConfig || action) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
              }}
            >
              {searchBlock}
              {actionBlock}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
